import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatInput } from '@/components/chat/ChatInput.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useMobile } from '@/hooks/useMobile.js';
import type { Contact } from '@/types/contact.js';

export function WelcomeScreen() {
  const navigate = useNavigate();
  const { chatService } = useServices();
  const isCreatingSession = useSessionStore(state => state.isCreatingSession);
  const isMobile = useMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);

  // Navigate to chat view immediately when session creation starts
  useEffect(() => {
    if (isCreatingSession) {
      console.log('[WelcomeScreen] Session creation started, navigating to chat/new');
      navigate('/chat/new');
    }
  }, [isCreatingSession, navigate]);

  // Detect width changes to switch between hero and simple layouts
  useEffect(() => {
    if (isMobile) return; // Skip for mobile

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Switch to simple layout if width is less than 700px (when artifacts are open)
        setIsNarrow(entry.contentRect.width < 700);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isMobile]);

  const handleSend = async (
    message: string,
    imageData?: { base64: string; mimeType: string },
    fileData?: { base64: string; mimeType: string; filename?: string },
    mentionedContacts?: Contact[]
  ) => {
    try {
      await chatService.sendMessage(message, imageData, fileData, mentionedContacts);
      // Navigation happens automatically via useEffect watching currentSessionId
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  // Mobile: Simple centered greeting with input at bottom
  if (isMobile) {
    return (
      <div className="flex flex-col h-full">
        {/* Centered greeting */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center space-y-3">
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">
              Hi, I'm Dexto
            </h1>
            <p className="text-sm text-muted-foreground">
              How can I help you today?
            </p>
          </div>
        </div>

        {/* Input at bottom */}
        <div className="p-4 pb-safe">
          <ChatInput
            onSend={handleSend}
            placeholder="Ask me anything..."
            variant="home"
          />
        </div>
      </div>
    );
  }

  // Desktop: Switch between hero and simple layout based on width
  if (isNarrow) {
    // Simple column layout when width is constrained (artifacts open)
    return (
      <div ref={containerRef} className="flex flex-col h-full">
        {/* Centered greeting */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center space-y-4 w-full max-w-md">
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">
              Hi, I'm Dexto
            </h1>
            <p className="text-base text-muted-foreground">
              How can I help you today?
            </p>
          </div>
        </div>

        {/* Input at bottom */}
        <div className="p-6 border-t border-border/50">
          <ChatInput
            onSend={handleSend}
            placeholder="Ask me anything..."
            variant="home"
          />
        </div>
      </div>
    );
  }

  // Desktop: Full hero layout
  return (
    <div ref={containerRef} className="flex items-center justify-center h-full p-8 bg-gradient-to-b from-background via-background to-muted/20">
      <div className="w-full max-w-3xl space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-5xl font-semibold text-foreground tracking-tight">
            Hi, I'm Dexto
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            What would you like to work on today?
          </p>
        </div>

        {/* Search-like Input with @ mention support */}
        <ChatInput
          onSend={handleSend}
          placeholder="Ask me anything..."
          variant="home"
        />
      </div>
    </div>
  );
}
